import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppRadioGroup from '@nxsys/components/AppRadioGroup';
import AppRadioReversed from '@nxsys/components/AppRadioReversed';
import { CompanyCreationMethodEnum } from '@nxsys/constants/AppEnums';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { AppIconType } from '@nxsys/types/icons';
import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import clsx from 'clsx';
import { Ref, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import IconWrapper from './IconWrapper';
import StepTitle from './StepTitle';

export type CompanyCreationMethodValues = {
  creationMethod: string;
};

export type CompanyCreationMethodFormRef = {
  isValidForm: () => Promise<boolean>;
  getValues: () => CompanyCreationMethodValues;
  resetFields: () => void;
};

type Props = {
  formRef: Ref<CompanyCreationMethodFormRef>;
  initialValues: Partial<CompanyCreationMethodValues>;
};

const CompanyCreationMethod: React.FC<Props> = ({ formRef, initialValues }) => {
  const { messages } = useTypedIntl();
  const { isBureau } = useSelector((state: AppState) => state.appStore);

  const options = [
    {
      label: isBureau
        ? messages['common.bureau.sendCompanyCreationLink']
        : messages['common.sendCompanyCreationLink'],
      description: messages['common.sendCompanyCreationLinkDescription'],
      value: CompanyCreationMethodEnum.SEND_COMPANY_CREATION_LINK,
      icon: 'IconLink',
    },
    {
      label: isBureau
        ? messages['common.bureau.createCompanyManually']
        : messages['common.createCompanyManually'],
      description: messages['common.createCompanyManuallyDescription'],
      value: CompanyCreationMethodEnum.CREATE_COMPANY_MANUALLY,
      icon: 'IconPenLine',
    },
  ];

  const [form] = Form.useForm<CompanyCreationMethodValues>();
  const watchValues = Form.useWatch([], form);

  useImperativeHandle(formRef, () => ({
    isValidForm: async () => {
      try {
        await form.validateFields();
        return true;
      } catch (error) {
        return false;
      }
    },
    getValues() {
      return form.getFieldsValue(true);
    },
    resetFields() {
      form.resetFields();
    },
  }));

  const onFinish: FormProps['onFinish'] = async (values) => {
    console.info('values', values);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <div className="CompanyCreationMethod">
      <StepTitle
        title={
          isBureau
            ? messages['common.bureau.chooseCompanyCreationMethod']
            : messages['common.chooseCompanyCreationMethod']
        }
        description={
          isBureau
            ? messages['common.bureau.chooseCompanyCreationMethodDescription']
            : messages['common.chooseCompanyCreationMethodDescription']
        }
      />
      <div className="space-y-[16px] mt-[24px]">
        <AppFormContainer
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          initialValues={{
            creationMethod: CompanyCreationMethodEnum.CREATE_COMPANY_MANUALLY,
            ...initialValues,
          }}
          layout="vertical"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <Form.Item name="creationMethod">
            <AppRadioGroup className="flex flex-col gap-y-[16px]">
              {options.map((option) => (
                <div
                  key={option.value}
                  className={clsx(
                    'border border-neutral-200 rounded-[12px] p-[16px]',
                    {
                      'border-primary-500':
                        option.value === watchValues?.creationMethod,
                    },
                  )}
                >
                  <AppRadioReversed value={option.value}>
                    <IconWrapper icon={option.icon as AppIconType} />
                    <div className="text-[16px] font-bold leading-none mb-[8px] mt-[16px]">
                      {option.label}
                    </div>
                    <div className="text-[14px] text-neutral-500">
                      {option.description}
                    </div>
                  </AppRadioReversed>
                </div>
              ))}
            </AppRadioGroup>
          </Form.Item>
        </AppFormContainer>
      </div>
    </div>
  );
};

export default CompanyCreationMethod;
